import React, { useMemo, useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useSelector } from 'react-redux';
import { Card } from '@ygtechnologies/rubus-ui';
import { IState } from '@/state/IState';
import { ConfiguratorGroupDto } from '@/services/configurator';
import ItemCategoryFilter from '@/components/filters/ItemCategoryFilter';
import ItemProviderFilter from '@/components/filters/ItemProviderFilter';
import { ItemCategory } from '@/types';
import theme from './RoomCategoryFilter.module.scss';
import { useParams } from 'react-router-dom';

interface RoomCategoryFilterProps {
    groups: ConfiguratorGroupDto[];
    onFilteredGroupsChange: (filteredGroups: ConfiguratorGroupDto[]) => void;
}

function extractCategoryIdsFromGroups(groups: ConfiguratorGroupDto[]): Set<number> {
    return new Set(
        groups
            .flatMap((group) => group.options)
            .map((option) => option.product?.itemCategoryId)
            .filter((id): id is number => id != null)
    );
}

function filterCategoriesToRelevant(
    categories: ItemCategory[],
    relevantIds: Set<number>
): ItemCategory[] {
    return categories
        .map((category) => ({
            ...category,
            subCategories: filterCategoriesToRelevant(category.subCategories || [], relevantIds),
        }))
        .filter((category) => relevantIds.has(category.id) || category.subCategories.length > 0);
}

function collectDescendantIds(category: ItemCategory, ids: Set<number>): void {
    ids.add(category.id);
    category.subCategories?.forEach((sub) => collectDescendantIds(sub, ids));
}

function buildMatchingCategoryIds(selectedIds: number[], allCategories: ItemCategory[]): Set<number> {
    const matchingIds = new Set<number>();

    const findAndCollect = (categories: ItemCategory[]) => {
        for (const category of categories) {
            if (selectedIds.includes(category.id)) {
                collectDescendantIds(category, matchingIds);
            } else {
                findAndCollect(category.subCategories || []);
            }
        }
    };

    findAndCollect(allCategories);
    return matchingIds;
}

function filterGroups(
    groups: ConfiguratorGroupDto[],
    matchingCategoryIds: Set<number>,
    selectedProviderIds: number[]
): ConfiguratorGroupDto[] {
    if (matchingCategoryIds.size === 0 && selectedProviderIds.length === 0) return groups;

    return groups.filter((group) => {
        const matchesCategory =
            matchingCategoryIds.size === 0 ||
            group.options.some(
                (option) => option.product?.itemCategoryId && matchingCategoryIds.has(option.product.itemCategoryId)
            );

        const matchesProvider =
            selectedProviderIds.length === 0 ||
            (group.providedByProfile?.id != null && selectedProviderIds.includes(group.providedByProfile.id));

        return matchesCategory && matchesProvider;
    });
}

export const RoomCategoryFilter: React.FC<RoomCategoryFilterProps> = ({ groups, onFilteredGroupsChange }) => {
    const { roomId } = useParams<{ roomId: string }>();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
    const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([]);
    const allItemCategories = useSelector((state: IState) => state.global.itemCategories);

    useEffect(() => {
        setSelectedCategoryIds([]);
        setSelectedProviderIds([]);
    }, [roomId]);

    const relevantCategoryIds = useMemo(() => extractCategoryIdsFromGroups(groups), [groups]);
    const filteredCategories = useMemo(
        () => filterCategoriesToRelevant(allItemCategories, relevantCategoryIds),
        [allItemCategories, relevantCategoryIds]
    );

    const matchingCategoryIds = useMemo(
        () => buildMatchingCategoryIds(selectedCategoryIds, allItemCategories),
        [selectedCategoryIds, allItemCategories]
    );

    const providers = useMemo(
        () =>
            Array.from(
                groups.reduce((map, group) => {
                    const profile = group.providedByProfile;
                    if (profile?.id != null) {
                        map.set(profile.id, profile.name ?? '');
                    }
                    return map;
                }, new Map<number, string>())
            ).map(([id, name]) => ({ id, name })),
        [groups]
    );

    const filteredGroups = useMemo(
        () => filterGroups(groups, matchingCategoryIds, selectedProviderIds),
        [groups, matchingCategoryIds, selectedProviderIds]
    );

    useEffect(() => {
        onFilteredGroupsChange(filteredGroups);
    }, [filteredGroups, onFilteredGroupsChange]);

    if (filteredCategories.length === 0 && providers.length === 0) return null;

    return (
        <Card className={theme.filterCard}>
            <div className={theme.filters}>
                {filteredCategories.length > 0 && (
                    <div className={theme.filterSection}>
                        <h4 className={theme.filterTitle}>
                            <Trans>Filter by category</Trans>
                        </h4>
                        <ItemCategoryFilter
                            itemCategories={filteredCategories}
                            maxDepth={1}
                            value={selectedCategoryIds}
                            onChange={setSelectedCategoryIds}
                        />
                    </div>
                )}

                {providers.length > 0 && (
                    <div className={theme.filterSection}>
                        <h4 className={theme.filterTitle}>
                            <Trans>Filter by provider</Trans>
                        </h4>
                        <ItemProviderFilter
                            providers={providers}
                            maxDepth={1}
                            value={selectedProviderIds}
                            onChange={setSelectedProviderIds}
                        />
                    </div>
                )}
            </div>
        </Card>
    );
};
